import { Button, ButtonProps } from '@mui/material';
import { useTranslation } from 'next-i18next';
import { FC, PropsWithChildren, useContext } from 'react';

import { IProductMetadata } from '@/features/pricingV2/domain';
import { PricingPageContext } from '@/features/pricingV2/ui/contexts';
import { PlanAction } from '@/features/common/pricing';

import { usePlanActions } from './hooks/usePlanActions';

import styles from './styles.module.scss';

const PlanCardActionButton: FC<
  PropsWithChildren<{
    color: ButtonProps['color'];
    variant?: ButtonProps['variant'];
    href?: ButtonProps['href'];
    onClick?: () => void;
  }>
> = ({ color, variant = 'contained', children, onClick, href }) => {
  return (
    <Button
      className={styles.action}
      size="large"
      fullWidth
      variant={variant}
      color={color}
      onClick={onClick}
      href={href}
    >
      {children}
    </Button>
  );
};

const TalkToSalesActionButton: FC<{
  tKey: string;
  onClick?: () => void;
  href?: string;
}> = ({ tKey, onClick, href }) => {
  const { t: tOverview } = useTranslation('pricingV2', {
    keyPrefix: tKey,
  });

  return (
    <PlanCardActionButton color="primary" onClick={onClick} href={href}>
      {tOverview('action_default')}
    </PlanCardActionButton>
  );
};

const GetStartedActionButton: FC<{
  tKey: string;
  onClick?: () => void;
  href?: string;
}> = ({ tKey, onClick, href }) => {
  const { t: tOverview } = useTranslation('pricingV2', {
    keyPrefix: tKey,
  });
  const { isTrialEnabled } = useContext(PricingPageContext);

  return (
    <PlanCardActionButton color="secondary" onClick={onClick} href={href}>
      {isTrialEnabled ? tOverview('action_trial') : tOverview('action_default')}
    </PlanCardActionButton>
  );
};

const GetStartedFreeActionButton: FC<{
  tKey: string;
  onClick?: () => void;
  href?: string;
}> = ({ tKey, onClick, href }) => {
  const { t: tOverview } = useTranslation('pricingV2', {
    keyPrefix: tKey,
  });
  const { isTrialEnabled } = useContext(PricingPageContext);

  return (
    <PlanCardActionButton color="secondary" onClick={onClick} href={href}>
      {isTrialEnabled ? tOverview('action_trial') : tOverview('action_default')}
    </PlanCardActionButton>
  );
};

const PlanCardActionFabric: FC<{
  tKey: string;
  action: PlanAction;
  onClick?: () => void;
  href?: string;
}> = ({ tKey, action, onClick, href }) => {
  switch (action) {
    case PlanAction.TalkToSales:
      return <TalkToSalesActionButton tKey={tKey} onClick={onClick} href={href} />;
    case PlanAction.GetStarted:
      return <GetStartedActionButton tKey={tKey} onClick={onClick} href={href} />;
    case PlanAction.GetStartedFree:
      return <GetStartedFreeActionButton tKey={tKey} onClick={onClick} href={href} />;
    default:
      return null;
  }
};

export const PlanCardAction: FC<{
  metadata: IProductMetadata;
  tKey: string;
}> = ({ metadata, tKey }) => {
  const { type, href, onClick } = usePlanActions({ metadata });

  return <PlanCardActionFabric tKey={tKey} action={type} href={href} onClick={onClick} />;
};
